/**
 * Design-Export für Fremdsysteme wie Legacy `design_export` (`include/module/design_export`): das veröffentlichte Design
 * mit Plattform-Platzhaltern für Titel, Beschreibung und Bilder in der Artikelbox, als Text, Datei oder Plenty-ZIP.
 *
 * Die Artikelbox baut `exportArticleHtml` Zeichen für Zeichen wie `action/get.php` (Z. 86-273). Einzige Abweichung: die
 * Zufallszahlen in Klassen und IDs (`rand()`) sind feste Zählwerte, damit gleiche Einstellungen gleichen Code ergeben.
 * Legacy-Schlüssel der gespeicherten Einstellungen (`pat_design.design_export.design_export_setting`) je Feld in Klammern.
 */
import { z } from 'zod';
import { ArticleExportGallery } from './articles.js';
import { HEADLINE_HIDDEN } from './designs.js';

import { DesignExportSettings, MAX_EXPORT_PICTURES, type DesignExportPlatform } from '@pat/core';
export { DesignExportSettings, DesignExportPlatform, DESIGN_EXPORT_PLATFORMS, MAX_EXPORT_PICTURES } from '@pat/core';

export const DesignExportRequest = z.object({ settings: DesignExportSettings });
export type DesignExportRequest = z.infer<typeof DesignExportRequest>;

/** Zuletzt benutzte Einstellungen des Designs (Legacy `design_export|index`); `null` = noch nie exportiert. */
export const DesignExportSettingsResponse = z.object({
  settings: DesignExportSettings.nullable(),
  /** Bearbeiter speichern die Einstellungen beim Export wie Legacy; Lesende exportieren ohne zu speichern. */
  canSave: z.boolean(),
});
export type DesignExportSettingsResponse = z.infer<typeof DesignExportSettingsResponse>;

/** Legacy `makeCss()` und `makeHtml()` getrennt; die SPA setzt Text, Datei oder ZIP daraus zusammen. */
export const DesignExportResponse = z.object({
  css: z.string(),
  html: z.string(),
  /** Exportiert wird die veröffentlichte Fassung. */
  designVersion: z.number().int().nonnegative(),
  rendererVersion: z.string(),
  warnings: z.array(z.string()),
  galleries: z.array(ArticleExportGallery),
  /** Einstellungen als zuletzt benutzte gespeichert (nur Bearbeiter, Legacy speicherte vor dem Export). */
  saved: z.boolean(),
});
export type DesignExportResponse = z.infer<typeof DesignExportResponse>;

/** Vorgaben je Plattform (Legacy `export_default_settings`); `{1}` steht für die Bildnummer ab 1. */
export const DESIGN_EXPORT_DEFAULTS: Record<DesignExportPlatform, {
  title: string;
  description: string;
  output: DesignExportSettings['output'];
  fileExtension?: DesignExportSettings['fileExtension'];
  pictureFormat?: DesignExportSettings['pictureFormat'];
  pictureCount: number;
  picturePlaceholder: string;
}> = {
  none: { title: '-', description: '-', output: 'text', pictureCount: 0, picturePlaceholder: '1' },
  afterbuy: { title: '<-StammName->', description: '<-StammBeschreibung->', output: 'text', pictureFormat: 'list', pictureCount: 1, picturePlaceholder: '<-StammBild->' },
  eazyAuction: { title: '$#Text.Auktionsname#$', description: '$#Text.Beschreibung#$', output: 'file', fileExtension: 'html', pictureCount: 4, picturePlaceholder: '$#Picture.{1}#$' },
  magnalister: { title: '#TITLE#', description: '#DESCRIPTION#', output: 'text', pictureCount: 3, picturePlaceholder: '#PICTURE{1}#' },
  plentyMarkets: { title: '$ListingTitle', description: '{% GetItemDescription() %}', output: 'file', fileExtension: 'html', pictureCount: 4, picturePlaceholder: '$ImageURL[{1}]' },
  dreamrobot: { title: '#artikelbezeichnung#', description: '#beschreibung#', output: 'text', pictureCount: 4, picturePlaceholder: '#bilderpfad_{1}#' },
};

/** Legacy `format_str(template, i, i + 1)`: `{0}` = Index, `{1}` = Bildnummer. */
export function picturePlaceholder(template: string, index: number): string {
  return template.replace(/\{(\d+)\}/g, (match, n: string) => (n === '0' ? String(index) : n === '1' ? String(index + 1) : match));
}

/** Neuer Export ohne gespeicherte Einstellungen: Formularvorgaben des Legacy-Wizards. */
export function initialExportSettings(): DesignExportSettings {
  return {
    platform: 'none', pictures: true, picturePosition: 'below', pictureFormat: 'list', title: '', description: '', picturePlaceholders: [],
    eazyAuctionPictureCount: 1, output: 'text', fileExtension: 'txt', plentyVersion: 7,
  };
}

/** Plattformwechsel wie `load_platform_defaults`: Platzhalter, Bildliste und Ausgabe der neuen Plattform, Rest bleibt. */
export function applyPlatformDefaults(settings: DesignExportSettings, platform: DesignExportPlatform): DesignExportSettings {
  if (platform === settings.platform) return settings;
  const d = DESIGN_EXPORT_DEFAULTS[platform];
  return {
    ...settings,
    platform,
    title: d.title,
    description: d.description,
    output: d.output,
    ...(d.fileExtension ? { fileExtension: d.fileExtension } : {}),
    ...(d.pictureFormat ? { pictureFormat: d.pictureFormat } : {}),
    picturePlaceholders: platform === 'eazyAuction' ? [] : Array.from({ length: d.pictureCount }, (_, i) => picturePlaceholder(d.picturePlaceholder, i)),
    ...(platform === 'eazyAuction' ? { eazyAuctionPictureCount: d.pictureCount } : {}),
  };
}

/** Feste Ersatzwerte für `rand(1111111, 9999999)` bzw. `rand(1111, 9999)`: eindeutig je Bild, gleich je Export. */
const pictureNumber = (key: number) => String(1111111 + key);
const RADIO_NAME = 'pat_picturegallery_open_1111';

/**
 * Inhalt der Artikelbox (Legacy `get.php` Z. 86-273); `undefined` bei „Nur Design ohne Beschreibung“ (Box entfällt).
 * Platzhalter bleiben roh, wie sie der Kunde eingibt.
 */
export function exportArticleHtml(s: DesignExportSettings): string | undefined {
  if (s.platform === 'none') return undefined;
  const eazy = s.platform === 'eazyAuction';
  const magna = s.platform === 'magnalister';
  const visibility = (key: number) => (eazy ? ` style="$#Sichtbarkeit.Picture:${key + 1}#$"` : '');
  // Magnalister ersetzt seinen Platzhalter selbst durch ein IMG-Tag.
  const image = (item: string) => (magna ? item : `<img src="${item}">`);
  const listImage = (item: string) => (magna ? item : `<img src="${item}" itemprop="image" border="0" hspace="0" vspace="0" />`);
  const hide = (selector: string, item: string) => (!eazy && !magna ? `<style>.${selector}${item}{display: none !important;}</style>` : '');
  let description = s.description;
  if (s.pictures) {
    let pictures: readonly string[] = [];
    if (eazy) {
      // „Hack“ des Altsystems: `$#Link.Picture:n#$` wirkt nur, wenn `$#Picture.n#$` irgendwo im Template steht.
      const count = Math.min(Math.max(s.eazyAuctionPictureCount, 0), MAX_EXPORT_PICTURES);
      if (count > 0) {
        description += ' <div style="display: none">';
        const links: string[] = [];
        for (let i = 1; i <= count; i++) {
          links.push(`$#Link.Picture:${i}#$`);
          description += `$#Picture.${i}#$ `;
        }
        description += '</div> ';
        pictures = links;
      }
    } else pictures = s.picturePlaceholders;
    let pictureHtml = '';
    if (s.pictureFormat !== 'list') {
      let menu = '';
      let pics = '';
      let first = true;
      pictures.forEach((item, key) => {
        if (item.trim() === '') return;
        const id = `pat_picturegallery_item_${pictureNumber(key)}`;
        const className = `pat_img_${pictureNumber(key)} pat_img_count_${key + 1}`;
        pics += `<input class="pat_picturegallery_open" type="radio" id="${id}" name="${RADIO_NAME}" aria-hidden="true" hidden=""${first ? ' checked="checked"' : ''}>`;
        first = false;
        pics += `<div class="pat_picturegallery_item ${className}"${visibility(key)}>${image(item)}</div>`;
        menu += `<li class="${className}"${visibility(key)}><label for="${id}">${image(item)}</label></li>`;
        menu += hide(className.replace(' ', '.'), item);
      });
      pics = `<div class="pat_picturegallery_item_wrapper"><div class="pat_picturegallery_item_inner_wrapper">${pics}</div></div>`;
      menu = `<div class="pat_picturegallery_ol_wrapper"><ol>${menu}</ol><div style="clear:both;"></div></div>`;
      pictureHtml = `<div class="pat_picturegallery">${s.pictureFormat === 'preview-top' ? menu + pics : pics + menu}</div>`;
    } else {
      pictureHtml = '<div class="pat_picture_container">';
      pictures.forEach((item, key) => {
        if (item.trim() === '') return;
        const className = `pat_img_${pictureNumber(key)}`;
        pictureHtml += `<div class="pat_picture_item ${className}"${visibility(key)}>${listImage(item)}</div>`;
        pictureHtml += hide(className, item);
      });
      pictureHtml += '</div>';
    }
    description = s.picturePosition === 'above' ? pictureHtml + description : description + pictureHtml;
  }
  if (s.title === '' || s.title === HEADLINE_HIDDEN) return `<div class="box-content">${description}</div>`;
  return `<div class="headline">${s.title}</div><div class="box-content">${description}</div>`;
}

/** Text- und Dateiausgabe wie `get.php` Z. 326-329: `<style>\n` CSS `\n</style>` HTML. */
export function exportText(parts: Pick<DesignExportResponse, 'css' | 'html'>): string {
  return `<style>\n${parts.css}\n</style>${parts.html}`;
}

/** Plenty-Seitengerüst, immer gleich (Legacy wie Beispiel der Plenty-Hilfe). */
export const PLENTY_HTML_STRUCTURE = '<!DOCTYPE html>\n<html>\n<head>\n    <meta charset="UTF-8">\n    <title>$ListingTitle</title>\n    {% GetCSSFile() %}\n</head>\n<body>\n    $MainFrame\n</body>\n</html>';

function base64Utf8(value: string): string {
  let binary = '';
  for (const byte of new TextEncoder().encode(value)) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/** Dateien im Plenty-ZIP (`get.php` Z. 278-316): Version 6 drei Textdateien, Version 7 `layout.xml` mit Base64-Teilen. */
export function plentyFiles(parts: Pick<DesignExportResponse, 'css' | 'html'>, version: 6 | 7): { name: string; content: string }[] {
  if (version === 6) {
    return [
      { name: 'css.txt', content: parts.css },
      { name: 'HtmlStructure.txt', content: PLENTY_HTML_STRUCTURE },
      { name: 'MainFrame.txt', content: parts.html },
    ];
  }
  const xml = '<?xml version="1.0" encoding="ISO-8859-1"?>\n' +
    '<ListingLayout><NumIndex index="0">' +
    `<htmlStructure>${base64Utf8(PLENTY_HTML_STRUCTURE)}</htmlStructure>` +
    `<mainFrame>${base64Utf8(parts.html)}</mainFrame>` +
    `<css>${base64Utf8(parts.css)}</css>` +
    '<appendixType>c3RhbmRhcmQ=</appendixType>' +
    '</NumIndex></ListingLayout>';
  return [{ name: 'layout.xml', content: xml }];
}

/** Dateiname wie Legacy `export_YYYYMMDD.<ext>`. */
export function exportFileName(now: Date, extension: 'txt' | 'html' | 'zip'): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `export_${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}.${extension}`;
}

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (const byte of bytes) crc = CRC_TABLE[(crc ^ byte) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

/** Unkomprimiertes ZIP (Methode 0, UTF-8-Namen); Legacy komprimierte mit Deflate, der Inhalt ist derselbe. */
export function zipStored(files: readonly { name: string; content: string }[], now: Date): Uint8Array<ArrayBuffer> {
  const encoder = new TextEncoder();
  const dosTime = (now.getHours() << 11) | (now.getMinutes() << 5) | (now.getSeconds() >> 1);
  const dosDate = ((now.getFullYear() - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate();
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  for (const file of files) {
    const name = encoder.encode(file.name);
    const data = encoder.encode(file.content);
    const crc = crc32(data);
    const local = new DataView(new ArrayBuffer(30));
    local.setUint32(0, 0x04034b50, true);
    local.setUint16(4, 20, true);
    local.setUint16(6, 0x0800, true);
    local.setUint16(8, 0, true);
    local.setUint16(10, dosTime, true);
    local.setUint16(12, dosDate, true);
    local.setUint32(14, crc, true);
    local.setUint32(18, data.length, true);
    local.setUint32(22, data.length, true);
    local.setUint16(26, name.length, true);
    local.setUint16(28, 0, true);
    const head = new DataView(new ArrayBuffer(46));
    head.setUint32(0, 0x02014b50, true);
    head.setUint16(4, 20, true);
    head.setUint16(6, 20, true);
    head.setUint16(8, 0x0800, true);
    head.setUint16(10, 0, true);
    head.setUint16(12, dosTime, true);
    head.setUint16(14, dosDate, true);
    head.setUint32(16, crc, true);
    head.setUint32(20, data.length, true);
    head.setUint32(24, data.length, true);
    head.setUint16(28, name.length, true);
    head.setUint32(42, offset, true);
    chunks.push(new Uint8Array(local.buffer), name, data);
    central.push(new Uint8Array(head.buffer), name);
    offset += 30 + name.length + data.length;
  }
  const centralSize = central.reduce((sum, part) => sum + part.length, 0);
  const end = new DataView(new ArrayBuffer(22));
  end.setUint32(0, 0x06054b50, true);
  end.setUint16(8, files.length, true);
  end.setUint16(10, files.length, true);
  end.setUint32(12, centralSize, true);
  end.setUint32(16, offset, true);
  const all = [...chunks, ...central, new Uint8Array(end.buffer)];
  const out = new Uint8Array(new ArrayBuffer(all.reduce((sum, part) => sum + part.length, 0)));
  let at = 0;
  for (const part of all) {
    out.set(part, at);
    at += part.length;
  }
  return out;
}
